import { isRetryableAuthError } from 'app/shared/utils/is-auth-error';
import { authTokenStore } from './auth-token-store';

/** Extra attempts allowed after an auth failure (retry exactly once). */
export const MAX_AUTH_RETRIES = 1;

/**
 * React Query `retry` predicate shared by every query and mutation. It retries
 * once, and only on an auth failure (a 401, or a 403 sent with an expired
 * token; see `isRetryableAuthError`), keeping the existing fast-fail behaviour
 * for all other errors.
 *
 * The refresh is kicked off here (not in the cache `onError`, which in v5 fires
 * only after retries are exhausted) so it is already in flight before the retry
 * runs. The retried request then picks up the fresh bearer because the
 * api-context token getters await the in-flight refresh via `authTokenStore`.
 */
export const retryOnAuthError = (failureCount: number, error: unknown): boolean => {
  const willRetry = isRetryableAuthError(error) && failureCount < MAX_AUTH_RETRIES;

  if (willRetry) {
    // Fire-and-forget: the store dedupes concurrent refreshes into one host
    // round-trip and never rejects, so the retried request just awaits the
    // fresh token via `authTokenStore.getToken()`. Forced, because the server
    // has already rejected the token we hold.
    authTokenStore.refresh({ force: true });
  }

  return willRetry;
};
